import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  type ApiResponseError,
  type ResponseError,
  fetchApiWithToken
} from '~/api/utils'
import { ToastType } from '~/components/Global/Interactions/Toast/Toast'
import { toast } from '~/utils'
import { toISOLocal } from '~/utils/date'
export type NewCallPayload = {
  applicants: number[]
  datetime: Date
}

export const useNewCallMutation = () => {
  return useMutation<NewCallPayload, ResponseError, NewCallPayload>({
    mutationFn: ({ applicants, datetime }) =>
      fetchApiWithToken({
        method: 'post',
        url: '/v1/actions/calls',
        data: {
          applicants: applicants.map((id) => `/v1/applicants/${id}`),
          datetime: toISOLocal(datetime)
        }
      }),
    onError: () => {
      toast(
        'Une erreur est survenue',
        'Une erreur est survenue lors de la création du rappel',
        ToastType.Danger
      )
    }
  })
}

type NewMessagePayload = {
  applicants: number[]
  agencyId: number
  message: string
}

export const useNewMessageMutation = () => {
  return useMutation<NewMessagePayload, ApiResponseError, NewMessagePayload>({
    mutationFn: ({ applicants, agencyId, message }) =>
      fetchApiWithToken({
        method: 'post',
        url: '/v1/sms',
        data: {
          applicants: applicants.map((id) => `/v1/applicants/${id}`),
          agency: `/v1/agencies/${agencyId}`,
          smsContent: message
        }
      })
  })
}

type EmailPayload = {
  applicants?: number[]
  contacts?: number[]
  emailTo: 'applicant' | 'contact'
  subject?: string
  content: string
  templateId?: number | null
  userId?: number | null
  orderId?: number | null
  attachments?: {
    filename: string
    base64_content: string
  }[]
  attachmentsIds?: number[]
  notificationRead?: boolean
  notificationReceived?: boolean
}

export const useNewEmailMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<EmailPayload, ApiResponseError, EmailPayload>({
    mutationFn: ({
      applicants,
      contacts,
      emailTo,
      userId,
      templateId,
      orderId,
      ...rest
    }) => {
      const applicantUris = applicants?.map((id) => `/v1/applicants/${id}`)
      const contactUris = contacts?.map((id) => `/v1/contacts/${id}`)
      const involved = emailTo === 'applicant' ? contactUris : applicantUris
      const recipients = emailTo === 'applicant' ? applicantUris : contactUris
      return fetchApiWithToken({
        method: 'post',
        url: '/v1/emails',
        data: {
          involved: involved,
          recipients: recipients,
          user: userId ? `/v1/users/${userId}` : null,
          template: templateId ? `/v1/templates/${templateId}` : null,
          order: orderId ? `/v1/orders/${orderId}` : null,
          ...rest
        }
      })
    },
    onSettled: (_, __, { orderId }) => {
      if (orderId) {
        // Email action can change applicant status on order
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }
    }
  })
}

export enum MeetingLocationType {
  ONLINE = 'online',
  AGENCY = 'agency',
  CONTACT = 'contact',
  CUSTOM = 'custom'
}

export type MeetingLocationData =
  | {
    type: MeetingLocationType.AGENCY
    agencyId?: number
  }
  | {
    type: MeetingLocationType.CONTACT
    contactId?: number
  }
  | {
    type: MeetingLocationType.CUSTOM
    address?: string
  }
  | {
    type: MeetingLocationType.ONLINE
    details?: string
  }

export type NewMeetingPayload = {
  fromDatetime: Date
  toDatetime: Date
  subject?: string
  location: MeetingLocationData | null
  wholeDay?: boolean
  isPrivate?: boolean
  notes?: string
  orderId?: string
  applicants: number[]
  contacts: number[]
}

export const useNewMeetingMutation = () => {
  const queryClient = useQueryClient()
  return useMutation<NewMeetingPayload, ApiResponseError, NewMeetingPayload>({
    mutationFn: ({
      fromDatetime,
      toDatetime,
      subject,
      location,
      wholeDay,
      isPrivate,
      notes,
      orderId,
      applicants,
      contacts
    }) => {
      let addressOf: string | undefined = undefined
      let customAddress: string | undefined = undefined

      if (location?.type === MeetingLocationType.AGENCY && location.agencyId) {
        addressOf = `/v1/agencies/${location.agencyId}`
      } else if (
        location?.type === MeetingLocationType.CONTACT &&
        location?.contactId
      ) {
        addressOf = `/v1/contacts/${location.contactId}`
      } else if (
        location?.type === MeetingLocationType.CUSTOM &&
        location?.address
      ) {
        customAddress = location.address
      } else if (location?.type === MeetingLocationType.ONLINE) {
        customAddress = location?.details ?? ''
      }

      return fetchApiWithToken({
        method: 'post',
        url: '/v1/events/meetings',
        data: {
          subject,
          fromDatetime: toISOLocal(fromDatetime),
          toDatetime: toISOLocal(toDatetime),
          wholeDay: wholeDay ?? false,
          private: isPrivate ?? false,
          addressOf,
          customAddress,
          notes,
          order: orderId ? `/v1/orders/${orderId}` : null,
          applicants: applicants.map((id) => `/v1/applicants/${id}`),
          contacts: contacts?.map((id) => `/v1/contacts/${id}`)
        }
      })
    },
    onSettled: (_, __, { orderId }) => {
      if (orderId) {
        // Meeting action can change applicant status on order
        queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      }
    }
  })
}
